import React, { useRef } from 'react';
import { Calendar as CalendarIcon, ChevronDown } from 'lucide-react';
import dayjs from 'dayjs';
import '../utils/dayjs-setup';
import type { DatePickerProps } from '../types';
import { useDateRangePicker } from '../hooks/useDateRangePicker';
import Calendar from './Calendar';
import QuickOptions from './QuickOptions';
import CustomRangeInputs from './CustomRangeInputs';

const GrafanaDatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  onTimezoneChange,
  placeholder = '选择日期范围',
  timezone = dayjs.tz.guess(),
  showTimezone = true,
  quickOptions,
  quickOptionGroups,
  className = '',
}) => {
  const pickerRef = useRef<HTMLDivElement>(null);
  const timezoneRef = useRef<HTMLDivElement>(null);

  const {
    isOpen,
    setIsOpen,
    selectedTab,
    setSelectedTab,
    tempRange,
    setTempRange,
    calendarMonth,
    setCalendarMonth,
    inputValue,
    timezoneOpen,
    setTimezoneOpen,
    hoveredDate,
    setHoveredDate,
    selectedQuickLabel,
    quickOptions: resolvedQuickOptions,
    quickOptionGroups: resolvedQuickOptionGroups,
    handleQuickSelect,
    handleCalendarSelect,
    handleInputChange,
    handleTimezoneChange,
    formatDisplayValue,
    handleApply,
  } = useDateRangePicker({
    value,
    onChange,
    onTimezoneChange,
    placeholder,
    timezone,
    quickOptions,
    quickOptionGroups,
    pickerRef,
    timezoneRef,
  });

  const displayValue = selectedQuickLabel ?? formatDisplayValue();

  return (
    <div className={`relative ${className}`} ref={pickerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 border rounded-lg bg-white hover:bg-gray-50 transition-colors min-w-[280px] text-left ${
          isOpen ? 'ring-2 ring-blue-500' : ''
        }`}
      >
        <CalendarIcon className="w-4 h-4 text-gray-500" />
        <span className="flex-1">{displayValue || placeholder}</span>
        <ChevronDown className="w-4 h-4 text-gray-500" />
      </button>

      {isOpen && (
        <div className="absolute top-full mt-1 w-[660px] bg-white border rounded-lg shadow-lg z-50">
          <div className="flex border-b">
            <button
              onClick={() => setSelectedTab('relative')}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                selectedTab === 'relative'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Quick range
            </button>
            <button
              onClick={() => setSelectedTab('absolute')}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                selectedTab === 'absolute'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Custom range
            </button>
          </div>

          <div className="flex">
            <div className="w-80 max-h-[400px] overflow-y-auto">
              {selectedTab === 'relative' && (
                <QuickOptions
                  options={resolvedQuickOptions}
                  groups={resolvedQuickOptionGroups}
                  onSelect={handleQuickSelect}
                />
              )}

              {selectedTab === 'absolute' && (
                <div className="border-r">
                  <Calendar
                    month={calendarMonth}
                    onMonthChange={setCalendarMonth}
                    tempRange={tempRange}
                    hoveredDate={hoveredDate}
                    onSelect={handleCalendarSelect}
                    onHover={setHoveredDate}
                  />
                </div>
              )}
            </div>

            <div className="flex-1 flex">
              <CustomRangeInputs
                tempRange={tempRange}
                onRangeChange={setTempRange}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                onApply={handleApply}
                onCancel={() => setIsOpen(false)}
                showTimezone={showTimezone}
                timezone={timezone}
                timezoneOpen={timezoneOpen}
                onTimezoneToggle={() => setTimezoneOpen(!timezoneOpen)}
                onTimezoneChange={handleTimezoneChange}
                timezoneRef={timezoneRef}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GrafanaDatePicker;
